import type { AsyncThunk } from "@reduxjs/toolkit"

import { fetchDoctorsList } from "../slices/doctors/doctorThunkApi"
import { getScannerConnectionStatus } from "../slices/integrations/integrationsThunkApi"
import { fetchFormLists } from "../slices/uassist/formThunkApi"
import {
  fetchCaseDispositions,
  fetchClinicalSettings,
  fetchUserPreferences,
  fetchWorkflowSettings,
  getCreditCardDetails,
  getPermissions,
  getSystemMessage,
  updateClinicalSettings,
  updateUserPreferences,
  updateWorkflowSettings,
} from "../slices/user/userApis"

/**
 * Cross-tab bootstrap cache (UDC-4323) — WHAT is cached and WHERE.
 *
 * Session-static bootstrap responses (permissions + org config) are persisted to
 * `localStorage` so a newly opened tab can hydrate Redux instantly instead of
 * re-fetching. Storage is shared across tabs, scoped per user_id + org_id, and
 * cleared on logout / org-switch. Kept fresh two ways: normal navigation
 * re-fetches and re-caches a dataset, or a `refreshedBy` write thunk triggers
 * an auto re-fetch on fulfilling. The server stays the source of truth.
 *
 * The runtime wiring (middleware, hydration, cross-tab sync) lives in
 * ./bootstrapCacheRuntime. This file holds the bits you are most likely to
 * hand-edit: the list of cached datasets and the storage helpers.
 */

// ---------------------------------------------------------------------------
// Which datasets are cached
// ---------------------------------------------------------------------------

export interface CacheableEntry {
  /** Stable cache key for this dataset. */
  key: string
  /** The RTK async-thunk that produces the data (payload treated as opaque JSON). */
  thunk: AsyncThunk<any, any, any>
  /**
   * Optional projection applied before the payload is written to localStorage.
   * Use it to cache only a safe subset (e.g. a status flag) and keep sensitive
   * fields out of storage. Hydration replays whatever was cached, so the result
   * must be a shape the thunk's `fulfilled` reducer tolerates.
   */
  project?: (payload: any) => unknown
  /**
   * Write thunks for this dataset. A write's own `fulfilled` isn't cache-tracked,
   * so without this the cache goes stale after an edit. When one fulfills, the
   * middleware re-dispatches `thunk` to refresh both state and cache.
   */
  refreshedBy?: AsyncThunk<any, any, any>[]
}

/**
 * Datasets safe to cache across tabs: session-static, non-PHI. Permissions (the
 * blocking gate) + the org-config fan-out fired from the header.
 *
 * PHI/sensitivity check (verified per payload — none contains patient data):
 *  - permissions      → permission-string list for the user+org.
 *  - doctors          → org assignee list (internal staff names/ids, not patients).
 *  - userPreferences  → the logged-in user's own UI prefs + country code.
 *  - systemMessage    → org-level system/marketing banners.
 *  - caseDispositions → static case-status lookup values.
 *  - formLists        → uAssist form template metadata.
 *  - clinicalSettings → org clinical config (tooth system, attachment stage, …).
 *  - workflowSettings → org workflow config (phases, flags).
 *  - scannerStatus    → integration connection status flags.
 *  - creditCardStatus → ONLY a status flag via `project` (never card number,
 *                       expiry, holder name, or payment-profile IDs).
 * Everything per-patient/per-case is excluded (must stay fresh).
 *
 * To add/remove a cached dataset, edit this list — and re-verify the payload has
 * no PHI/sensitive fields (add a `project` to strip them if needed).
 */
export const CACHEABLE: CacheableEntry[] = [
  // No known write path for these from within this app - refreshedBy stays empty.
  { key: "permissions", thunk: getPermissions },
  { key: "doctors", thunk: fetchDoctorsList },
  {
    key: "userPreferences",
    thunk: fetchUserPreferences,
    refreshedBy: [updateUserPreferences],
  },
  { key: "systemMessage", thunk: getSystemMessage },
  { key: "caseDispositions", thunk: fetchCaseDispositions },
  { key: "formLists", thunk: fetchFormLists },
  {
    key: "clinicalSettings",
    thunk: fetchClinicalSettings,
    refreshedBy: [updateClinicalSettings],
  },
  {
    key: "workflowSettings",
    thunk: fetchWorkflowSettings,
    refreshedBy: [updateWorkflowSettings],
  },
  // scannerStatus/creditCardStatus already re-fetch after their own updates
  // elsewhere - no refreshedBy here to avoid a duplicate refresh.
  { key: "scannerStatus", thunk: getScannerConnectionStatus },
  // Cache only the card status used to gate the payment prompt — the Settings /
  // Order screens still fetch full card details on demand. `project` drops
  // card_details + payment-profile IDs so they never touch localStorage.
  {
    key: "creditCardStatus",
    thunk: getCreditCardDetails,
    project: (payload) => ({
      status: payload?.status,
      result: { status: payload?.result?.status },
    }),
  },
]

/** Find the cached dataset a given fulfilled action type belongs to. */
export const findCacheableByFulfilledType = (
  actionType: string,
): CacheableEntry | undefined =>
  CACHEABLE.find((entry) => entry.thunk.fulfilled.type === actionType)

/** Find the cached dataset a given write-thunk's fulfilled action should refresh. */
export const findCacheableByRefreshTriggerType = (
  actionType: string,
): CacheableEntry | undefined =>
  CACHEABLE.find((entry) =>
    entry.refreshedBy?.some((thunk) => thunk.fulfilled.type === actionType),
  )

// ---------------------------------------------------------------------------
// localStorage helpers (scoped per user + org)
// ---------------------------------------------------------------------------

// Bump when a cached payload shape changes; old entries become unreadable and
// are dropped (then GC'd by clearBootstrapCache).
export const SCHEMA_VERSION = 1

const KEY_PREFIX = `udc.bootstrap.v${SCHEMA_VERSION}`
// Broad prefix used only for wiping every version on logout / org-switch.
const CLEAR_PREFIX = "udc.bootstrap."

const storageKey = (userId: string, orgId: string): string =>
  `${KEY_PREFIX}.${userId}.${orgId}`

export type BootstrapCacheBlob = Record<string, unknown>

/** Read the whole cache blob for a user+org, or null on miss/parse error. */
export const readBootstrapCache = (
  userId?: string,
  orgId?: string,
): BootstrapCacheBlob | null => {
  if (!userId || !orgId) return null
  try {
    const raw = localStorage.getItem(storageKey(userId, orgId))
    return raw ? (JSON.parse(raw) as BootstrapCacheBlob) : null
  } catch {
    // Corrupt/unavailable storage → treat as a cache miss.
    return null
  }
}

/** Persist a single dataset payload into the user+org blob. Best-effort. */
export const writeBootstrapCacheEntry = (
  userId: string | undefined,
  orgId: string | undefined,
  key: string,
  payload: unknown,
): void => {
  if (!userId || !orgId) return
  try {
    const blob = readBootstrapCache(userId, orgId) ?? {}
    blob[key] = payload
    localStorage.setItem(storageKey(userId, orgId), JSON.stringify(blob))
  } catch {
    // Quota exceeded / serialization failure — cache is best-effort, ignore.
  }
}

/** Remove every bootstrap-cache entry (all users/orgs/versions). */
export const clearBootstrapCache = (): void => {
  try {
    const toRemove: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key && key.startsWith(CLEAR_PREFIX)) toRemove.push(key)
    }
    toRemove.forEach((key) => localStorage.removeItem(key))
  } catch {
    // ignore
  }
}
